import { CREATURE_FILTER_LAYERS, creatureFilterResults, type CreatureFilterSelection } from '../janus-engine/authoring/creature-filter';

export const CREATURE_FILTER_CONTROLS = ['sources', 'cr_range_begin', 'cr_range_end', ...CREATURE_FILTER_LAYERS,
    'ft_list_source_filter', 'input_search_creature', 'encounter_creature_only_owned_cb'] as const;

/** Use the same control reader as queryDataInputs, including Tom Select arrays and checkbox booleans. */
export function readCreatureFilterSelection(): CreatureFilterSelection {
    const root = document.querySelector<HTMLElement>('#div_add_encounter_creatures_table [data-ft-inline-search="1"]');
    return { ...Object.fromEntries(['sources', 'cr_range_begin', 'cr_range_end', ...CREATURE_FILTER_LAYERS].map(key => [key, getElementValue(key)])),
        source_id: getElementValue('ft_list_source_filter'), keyword: getElementValue('input_search_creature'),
        only_owned: getElementValue('encounter_creature_only_owned_cb'), campaign_id: root?.dataset.ftCampaignId ?? '' };
}

export async function runCreatureFilter(target: HTMLElement, selection: CreatureFilterSelection, current: () => boolean,
    render: (value: unknown) => void): Promise<void> {
    target.textContent = 'Filtering creatures…';
    target.setAttribute('aria-busy', 'true');
    try {
        const response = await fetch(BASE_URL + 'options/search/filter_creatures_inc.php', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(selection),
        });
        if (!current()) return;
        if (!response.ok) throw new Error(`Filter inputs unavailable (${response.status}). Please retry.`);
        const result = await creatureFilterResults(await response.json(), selection, current);
        if (!result || !current()) return;
        render(result);
        const note = document.createElement('div');
        note.className = 'text-muted small px-2 mb-1';
        note.setAttribute('data-ft-ec-filter-note', '');
        note.dataset.candidateCount = String(result.candidate_count);
        note.dataset.compileCount = String(result.compile_count);
        note.textContent = result.match_count > result.results.length
            ? `Showing the first ${result.results.length} of ${result.match_count} matches — narrow the filter or type a name to see the rest.`
            : `${result.match_count} matching creatures.`;
        if (result.unchecked.length) {
            const details = document.createElement('details'), summary = document.createElement('summary'), list = document.createElement('ul');
            details.setAttribute('data-ft-ec-filter-unchecked', '');
            summary.textContent = `${result.unchecked.length} ${result.unchecked.length === 1 ? 'creature' : 'creatures'} could not be checked.`;
            for (const row of result.unchecked) {
                const item = document.createElement('li');
                item.textContent = row.label || 'Unnamed creature';
                item.title = row.reason;
                list.append(item);
            }
            details.append(summary, list);
            note.append(details);
        }
        target.prepend(note);
    } catch (error) {
        if (!current()) return;
        target.textContent = 'Filter unavailable. Please retry.';
        displayWarning(error instanceof Error ? error.message : 'Filter unavailable. Please retry.');
    } finally {
        if (current()) target.removeAttribute('aria-busy');
    }
}
